import { useQuery } from "@rocicorp/zero/react";
import {
  createFileRoute,
  Navigate,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";

import { AppSidebar } from "@/components/app-sidebar";
import { Header } from "@/components/header";
import { SidebarProvider } from "@/components/ui/sidebar";
import { WorkspaceProvider } from "@/features/workspaces/store/workspace.store";
import { useZero } from "@/hooks/use-zero";
import { cn } from "@/lib/utils";
import { RouterContext } from "@/router";

export const Route = createFileRoute("/_app/w/$slug")({
  component: RouteComponent,
});

function RouteComponent() {
  const { slug } = Route.useParams();
  const zero = useZero();
  const [workspace, result] = useQuery(
    zero.query.workspaces.where("slug", slug).related("boards").one(),
  );

  const matches = useRouterState({ select: (s) => s.matches });
  const layoutOptions = matches.reduce((acc, match) => {
    if (match.context.layoutOptions) {
      return { ...acc, ...match.context.layoutOptions };
    }
    return acc;
  }, defaultLayoutOptions);

  if (!workspace) {
    return result.type === "complete" ? <Navigate to="/w" /> : null;
  }

  return (
    <WorkspaceProvider workspace={workspace}>
      <SidebarProvider>
        <AppSidebar />
        <div className="h-full flex flex-col grow overflow-x-hidden">
          <Header className="px-3 mb-2" />
          <main
            className={cn(
              "mx-3 flex flex-col grow min-h-0",
              layoutOptions.safeArea && "pb-safe",
            )}
          >
            <Outlet />
          </main>
        </div>
      </SidebarProvider>
    </WorkspaceProvider>
  );
}

const defaultLayoutOptions: Required<
  NonNullable<RouterContext["layoutOptions"]>
> = {
  safeArea: false,
};
